import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { API_BASE } from '../lib/api-client';
import { Button, Logo } from '../components/primitives';

/**
 * Phase 2.5 — 사장 자가 가입 폼 (MVP).
 *
 * 흐름: form 입력 → POST /v1/auth/signup → 201 → "운영자 승인 후 로그인 가능" 안내.
 * 운영자가 admin SPA 에서 /v1/admin/signups/:id/approve → Company + User + Membership 트랜잭션 생성.
 * 본 폼은 인증 X (공개 endpoint). 보안 강화 (captcha 등) 는 별개 trace.
 *
 * MVP fields: email / password (min 8) / name / companyName / phone (optional).
 * companySlug / businessNo 는 backend 자동 처리. UI 노출 X.
 */
export function SignupRoute() {
  const auth = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  // 이미 로그인된 사용자는 본 폼 진입 차단 (login.tsx 패턴).
  if (auth.status === 'auth') {
    if (auth.role === 'admin') {
      return <Navigate to="/" replace state={{ from: location }} />;
    }
    if (auth.role === 'owner' || auth.role === 'staff') {
      return <Navigate to="/ops" replace state={{ from: location }} />;
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErrorMsg(null);
    try {
      const res = await fetch(`${API_BASE}/v1/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          name,
          companyName,
          phone: phone || undefined,
        }),
      });
      if (res.status === 201) {
        setSubmitted(true);
        return;
      }
      // 4xx 처리: 409 (중복) / 400 (validation) / 기타
      let serverMsg: string | null = null;
      try {
        const body = await res.json();
        serverMsg =
          (body && (body.message || body.error)) ||
          (Array.isArray(body?.message) ? body.message.join(', ') : null);
      } catch {
        // body 파싱 실패 시 fallback
      }
      if (res.status === 409) {
        setErrorMsg(serverMsg ?? '이미 신청 중이거나 등록된 이메일입니다.');
      } else if (res.status === 400) {
        setErrorMsg(serverMsg ?? '입력값을 확인해 주세요.');
      } else {
        setErrorMsg(serverMsg ?? `가입 신청에 실패했습니다 (HTTP ${res.status})`);
      }
    } catch (err) {
      setErrorMsg((err as Error).message ?? '네트워크 오류로 가입 신청에 실패했습니다.');
    } finally {
      setBusy(false);
    }
  }

  if (submitted) {
    return (
      <main
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          background: 'var(--bg-page)',
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 380,
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-weak)',
            borderRadius: 'var(--radius-xl)',
            boxShadow: 'var(--shadow-md)',
            padding: 28,
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Logo size={26} />
            <span
              style={{
                fontSize: 11,
                color: 'var(--fg-3)',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              사장 콘솔
            </span>
          </div>
          <div>
            <h1
              style={{
                fontSize: 22,
                fontWeight: 700,
                margin: 0,
                letterSpacing: '-0.02em',
                color: 'var(--fg-1)',
              }}
            >
              가입 신청 접수됨
            </h1>
            <p
              style={{
                fontSize: 13,
                color: 'var(--fg-3)',
                marginTop: 8,
                marginBottom: 0,
                lineHeight: 1.5,
              }}
            >
              운영자 승인 후 로그인하실 수 있습니다. 승인 결과는 별도로 안내 드립니다.
            </p>
          </div>
          <Link
            to="/login"
            style={{
              display: 'inline-block',
              textAlign: 'center',
              padding: '10px 12px',
              border: '1px solid var(--border-base)',
              borderRadius: 8,
              fontSize: 14,
              color: 'var(--fg-1)',
              textDecoration: 'none',
              background: 'var(--bg-surface)',
            }}
          >
            로그인 화면으로
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background: 'var(--bg-page)',
      }}
    >
      <form
        onSubmit={onSubmit}
        style={{
          width: '100%',
          maxWidth: 380,
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-weak)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-md)',
          padding: 28,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Logo size={26} />
          <span
            style={{
              fontSize: 11,
              color: 'var(--fg-3)',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            사장 콘솔
          </span>
        </div>
        <div>
          <h1
            style={{
              fontSize: 22,
              fontWeight: 700,
              margin: 0,
              letterSpacing: '-0.02em',
              color: 'var(--fg-1)',
            }}
          >
            가입 신청
          </h1>
          <p
            style={{
              fontSize: 13,
              color: 'var(--fg-3)',
              marginTop: 6,
              marginBottom: 0,
            }}
          >
            운영자 승인 후 로그인하실 수 있습니다.
          </p>
        </div>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--fg-2)' }}>이메일</span>
          <input
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{
              padding: '10px 12px',
              border: '1px solid var(--border-base)',
              borderRadius: 8,
              fontSize: 14,
              background: 'var(--bg-surface)',
              color: 'var(--fg-1)',
            }}
          />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--fg-2)' }}>
            비밀번호 (8자 이상)
          </span>
          <input
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={{
              padding: '10px 12px',
              border: '1px solid var(--border-base)',
              borderRadius: 8,
              fontSize: 14,
              background: 'var(--bg-surface)',
              color: 'var(--fg-1)',
            }}
          />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--fg-2)' }}>이름 (사장)</span>
          <input
            type="text"
            autoComplete="name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{
              padding: '10px 12px',
              border: '1px solid var(--border-base)',
              borderRadius: 8,
              fontSize: 14,
              background: 'var(--bg-surface)',
              color: 'var(--fg-1)',
            }}
          />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--fg-2)' }}>회사명</span>
          <input
            type="text"
            autoComplete="organization"
            required
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            style={{
              padding: '10px 12px',
              border: '1px solid var(--border-base)',
              borderRadius: 8,
              fontSize: 14,
              background: 'var(--bg-surface)',
              color: 'var(--fg-1)',
            }}
          />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--fg-2)' }}>
            전화번호 (선택)
          </span>
          <input
            type="tel"
            autoComplete="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="010-1234-5678"
            style={{
              padding: '10px 12px',
              border: '1px solid var(--border-base)',
              borderRadius: 8,
              fontSize: 14,
              background: 'var(--bg-surface)',
              color: 'var(--fg-1)',
            }}
          />
        </label>

        {errorMsg && (
          <div
            style={{
              fontSize: 13,
              color: 'var(--danger)',
              background: 'var(--danger-bg)',
              borderRadius: 8,
              padding: '8px 12px',
            }}
          >
            {errorMsg}
          </div>
        )}

        <Button
          type="submit"
          variant="strong"
          size="lg"
          disabled={busy || !email || !password || !name || !companyName}
        >
          {busy ? '신청 중…' : '가입 신청'}
        </Button>

        <div
          style={{
            fontSize: 12,
            color: 'var(--fg-3)',
            textAlign: 'center',
          }}
        >
          이미 계정이 있으신가요?{' '}
          <Link to="/login" style={{ color: 'var(--fg-1)', fontWeight: 600 }}>
            로그인
          </Link>
        </div>
      </form>
    </main>
  );
}
